import { starterMetrics } from "../data";
import type { Metric, UserSession } from "../types";
import type { DataDomain } from "./dataDomain";

// Dashboards are deliberately absent. They live in the API so that a
// dashboard saved without the user pressing anything survives a cleared cache
// and appears on their next machine, which browser storage cannot offer.
const KEYS = {
  session: "dmi.analytics.session",
  metrics: "dmi.analytics.metrics",
  context: "dmi.analytics.selected-context",
  assistant: "dmi.analytics.selected-assistant",
} as const;

/** The governed contract the user chose, which every turn is pinned to. */
export interface SelectedContext {
  contextId: string;
  contextVersion: string;
  name: string;
  /** Absent on selections saved before sources were classified. */
  dataDomain?: DataDomain | null;
}

function parse<T>(raw: string | null, fallback: T): T {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

export const storage = {
  session(): UserSession | null {
    const session = parse<UserSession | null>(
      sessionStorage.getItem(KEYS.session),
      null,
    );
    if (!session || new Date(session.expiresAt).getTime() <= Date.now()) {
      sessionStorage.removeItem(KEYS.session);
      return null;
    }
    return session;
  },

  saveSession(session: UserSession): void {
    sessionStorage.setItem(KEYS.session, JSON.stringify(session));
  },

  clearSession(): void {
    sessionStorage.removeItem(KEYS.session);
  },

  metrics(): Metric[] {
    return parse<Metric[]>(
      localStorage.getItem(KEYS.metrics),
      starterMetrics,
    );
  },

  saveMetrics(metrics: Metric[]): void {
    localStorage.setItem(KEYS.metrics, JSON.stringify(metrics));
  },

  selectedContext(): SelectedContext | null {
    return parse<SelectedContext | null>(
      localStorage.getItem(KEYS.context),
      null,
    );
  },

  saveSelectedContext(context: SelectedContext): void {
    localStorage.setItem(KEYS.context, JSON.stringify(context));
  },

  /** The assistant chosen on the landing page, so returning users skip it. */
  selectedAssistant(): string | null {
    return localStorage.getItem(KEYS.assistant);
  },

  saveSelectedAssistant(assistant: string): void {
    localStorage.setItem(KEYS.assistant, assistant);
  },
};
